import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import SEO from '../components/SEO';
import BlogCard from '../components/BlogCard';
import { getCategoryBySlug, getPosts } from '../services/api';
import config from '../config';

const CategoryPage = () => {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [category, setCategory] = useState(null);
  const [posts, setPosts] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const currentPage = parseInt(searchParams.get('page') || '1');

  useEffect(() => {
    fetchData();
  }, [slug, currentPage]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [categoryRes, postsRes] = await Promise.all([
        getCategoryBySlug(slug),
        getPosts({ category: slug, page: currentPage, limit: config.POSTS_PER_PAGE })
      ]);

      if (categoryRes.success) setCategory(categoryRes.data);
      if (postsRes.success) {
        setPosts(postsRes.data);
        setPagination(postsRes.pagination);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setSearchParams({ page: page.toString() });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600"></div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Category Not Found</h1>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title={category.meta_title || `${category.name} - Blog`}
        description={category.meta_description || `Browse all articles in ${category.name} category. ${category.description}`}
        keywords={`${category.name}, blog, articles, ${category.tags}, ${category.name} posts, ${category.name} insights`}
        image={`${config.SITE_URL}/og-image.jpg`}
        type="website"
        url={`${config.SITE_URL}/category/${category.slug}${currentPage > 1 ? `?page=${currentPage}` : ''}`}
      />

      <div 
        className="py-20"
        style={{ 
          background: `linear-gradient(135deg, ${category.color}20 0%, ${category.color}40 100%)` 
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <div className="text-6xl mb-4 animate-scale-in">{category.icon}</div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 animate-slide-down">
            {category.name}
          </h1>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto animate-slide-up">
            {category.description}
          </p>
          <p className="text-gray-600 mt-4 animate-slide-up">
            {category.post_count} articles in this category
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16">
        {posts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-xl text-gray-600">No posts found in this category.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <div
                  key={post.id}
                  className="animate-slide-up"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <BlogCard post={post} />
                </div>
              ))}
            </div>

            {pagination && pagination.pages > 1 && (
              <div className="mt-12 flex justify-center items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg bg-white border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                >
                  <ChevronLeft size={20} />
                </button>

                {[...Array(pagination.pages)].map((_, index) => {
                  const page = index + 1;
                  if (
                    page === 1 ||
                    page === pagination.pages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-4 py-2 rounded-lg font-semibold transition ${
                          currentPage === page
                            ? 'bg-blue-600 text-white'
                            : 'bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return <span key={page} className="px-2">...</span>;
                  }
                  return null;
                })}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === pagination.pages}
                  className="p-2 rounded-lg bg-white border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default CategoryPage;
